import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, DollarSign, Calendar, Activity, Bed, MessageSquare, Settings,
  LogOut, PieChart as PieChartIcon, BarChart3, Clock, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Eye, FileText, Inbox
} from 'lucide-react';
import './AdminHotelApprovals.css';

function AdminHotelApprovals() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser, userProfile } = useAuth();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [declineModal, setDeclineModal] = useState({ open: false, id: null });
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState('');
  const [reasonModal, setReasonModal] = useState({ open: false, reason: '' });

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const idToken = await currentUser.getIdToken();
      const res = await fetch('/api/admin/hotel-registrations?status=pending', {
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) setPending(data.data);
      else setError(data.message || 'Failed to load pending.');
    } catch (e) { setError('Failed to load pending.'); }
    setLoading(false);
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const idToken = await currentUser.getIdToken();
      const res = await fetch('/api/admin/hotel-registrations/history', {
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) setHistory(data.data);
      else setError(data.message || 'Failed to load history.');
    } catch (e) { setError('Failed to load history.'); }
    setLoading(false);
  };

  const handleApprove = async (id) => {
    setLoading(true);
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const idToken = await currentUser.getIdToken();
      await fetch(`/api/admin/hotel-registrations/${id}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: 'include',
      });
      setPending(p => p.filter(h => h.owner_id !== id));
    } catch (e) { setError('Failed to approve.'); }
    setLoading(false);
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) return;
    setLoading(true);
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const idToken = await currentUser.getIdToken();
      await fetch(`/api/admin/hotel-registrations/${declineModal.id}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({ reason: declineReason }),
      });
      setPending(p => p.filter(h => h.owner_id !== declineModal.id));
      await fetchHistory();
      setDeclineModal({ open: false, id: null });
      setDeclineReason('');
    } catch (e) { setError('Failed to decline.'); }
    setLoading(false);
  };

  const openDecline = (id) => setDeclineModal({ open: true, id });
  const closeDecline = () => { setDeclineModal({ open: false, id: null }); setDeclineReason(''); };
  const openDetail = (row) => navigate(`/admin/approvals/${row.id}`);

  const switchTab = (t) => {
    setTab(t);
    if (t === 'history' && history.length === 0) fetchHistory();
  };

  const adminName = userProfile?.fullName || userProfile?.full_name || 'Admin';

  const navItems = [
    { path: '/admin',               Icon: PieChartIcon,  label: 'Analytics' },
    { path: '/admin/approvals',     Icon: Clock,         label: 'Hotel approvals' },
    { path: '/admin/hotel',         Icon: Bed,           label: 'Hotel management' },
    { path: '/admin/users',         Icon: Users,         label: 'User management' },
    { path: '/admin/notifications', Icon: MessageSquare, label: 'Notifications' },
    { path: '/iot-management',      Icon: Activity,      label: 'IoT management' },
    { path: '/admin/bookings',      Icon: Calendar,      label: 'Bookings' },
    { path: '/admin/revenue',       Icon: DollarSign,    label: 'Revenue' },
    { path: '/admin/reports',       Icon: BarChart3,     label: 'Reports' },
    { path: '/admin/settings',      Icon: Settings,      label: 'Settings' },
  ];

  const isActive = (path) => path === '/admin'
    ? location.pathname === '/admin'
    : location.pathname.startsWith(path);

  const renderRows = (rows, isPendingTab) => (
    <div className="ib-card approvals-table-card">
      <table className="ib-table approvals-table">
        <thead>
          <tr>
            <th>Property</th>
            <th>Owner</th>
            <th>Submitted</th>
            {!isPendingTab && <th>Status</th>}
            <th className="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.owner_id || row.id}>
              <td>
                <div className="cell-primary">{row.hotel_name}</div>
                <div className="cell-secondary">ID #{row.id || row.owner_id}</div>
              </td>
              <td>
                <div className="cell-primary">{row.owner_name}</div>
                <div className="cell-secondary">{row.owner_email}</div>
              </td>
              <td className="cell-muted">
                {isPendingTab
                  ? new Date(row.submitted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                  : (row.reviewed_at ? new Date(row.reviewed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}
              </td>
              {!isPendingTab && (
                <td>
                  <span className={`ib-pill ib-pill--${row.status === 'approved' ? 'positive' : row.status === 'declined' ? 'negative' : 'pending'}`}>
                    {row.status}
                  </span>
                  {row.status === 'declined' && row.decline_reason && (
                    <button
                      className="reason-link"
                      onClick={() => setReasonModal({ open: true, reason: row.decline_reason })}
                    >
                      View reason
                    </button>
                  )}
                </td>
              )}
              <td className="col-actions">
                <button className="ib-btn ib-btn--ghost ib-btn--sm" onClick={() => openDetail(row)}>
                  <Eye size={14} /> View
                </button>
                {isPendingTab && (
                  <>
                    <button className="ib-btn ib-btn--gold ib-btn--sm" onClick={() => handleApprove(row.owner_id)}>
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <button className="ib-btn ib-btn--danger ib-btn--sm" onClick={() => openDecline(row.owner_id)}>
                      <XCircle size={14} /> Decline
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const rows = tab === 'pending' ? pending : history;

  return (
    <div className="admin-dashboard admin-page">
      {/* Sidebar (matches AdminDashboard) */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark" aria-hidden="true">IB</span>
            {sidebarOpen && (
              <span className="brand-wordmark">
                InnBotics
                <span className="brand-eyebrow">Admin Console</span>
              </span>
            )}
          </div>
          <button
            className="toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Admin navigation">
          {navItems.map(({ path, Icon, label }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
              title={!sidebarOpen ? label : undefined}
            >
              <span className="nav-icon"><Icon size={18} strokeWidth={1.75} /></span>
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={async () => { await logout(); navigate('/login'); }} className="logout-btn">
            <span className="nav-icon"><LogOut size={18} strokeWidth={1.75} /></span>
            {sidebarOpen && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <header className="ib-page-header">
          <div className="header-left">
            <p className="ib-eyebrow">Operations · Approvals</p>
            <h1 className="ib-display">Hotel registrations</h1>
            <p className="ib-lede">Review applications and admit new properties to the InnBotics network.</p>
          </div>
          <div className="header-right">
            <div className="ib-tabs" role="tablist">
              <button className={tab === 'pending' ? 'active' : ''} onClick={() => switchTab('pending')} role="tab">
                <FileText size={14} /> Pending
                <span className="ib-tab-count">{pending.length}</span>
              </button>
              <button className={tab === 'history' ? 'active' : ''} onClick={() => switchTab('history')} role="tab">
                <Clock size={14} /> History
                {history.length > 0 && <span className="ib-tab-count">{history.length}</span>}
              </button>
            </div>
          </div>
        </header>

        {error && <div className="approval-error">{error}</div>}

        {loading ? (
          <div className="ib-card ib-card--padded">
            <div className="approval-loading">
              <div className="approval-spinner" aria-hidden="true" />
              <p>Loading registrations…</p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="ib-card ib-card--padded">
            <div className="ib-empty">
              <Inbox size={36} strokeWidth={1.5} color="#94A3B8" />
              <h3>{tab === 'pending' ? 'No pending applications' : 'No history yet'}</h3>
              <p>{tab === 'pending' ? 'New submissions will appear here for review.' : 'Approved or declined registrations will appear here.'}</p>
            </div>
          </div>
        ) : (
          renderRows(rows, tab === 'pending')
        )}

        {reasonModal.open && (
          <div className="ib-modal" onClick={() => setReasonModal({ open: false, reason: '' })}>
            <div className="ib-modal-content" onClick={e => e.stopPropagation()}>
              <header className="ib-modal-header">
                <p className="ib-eyebrow">Decline reason</p>
                <h3 className="ib-display">Why this was declined</h3>
              </header>
              <div className="ib-modal-body">{reasonModal.reason}</div>
              <div className="ib-modal-actions">
                <button className="ib-btn ib-btn--ghost" onClick={() => setReasonModal({ open: false, reason: '' })}>Close</button>
              </div>
            </div>
          </div>
        )}

        {declineModal.open && (
          <div className="ib-modal" onClick={closeDecline}>
            <div className="ib-modal-content" onClick={e => e.stopPropagation()}>
              <header className="ib-modal-header">
                <p className="ib-eyebrow">Decline registration</p>
                <h3 className="ib-display">Share feedback with the owner</h3>
                <p className="ib-lede">The owner will see this message and may revise and resubmit.</p>
              </header>
              <textarea
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                placeholder="Outline what's missing, unclear, or out of policy…"
                className="ib-textarea"
              />
              <div className="ib-modal-actions">
                <button className="ib-btn ib-btn--ghost" onClick={closeDecline}>Cancel</button>
                <button
                  className="ib-btn ib-btn--danger"
                  onClick={handleDecline}
                  disabled={!declineReason.trim()}
                >
                  <XCircle size={14} /> Submit decline
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminHotelApprovals;
